import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { getRedemptionRequestsList } from '../services/subscriberWalletService';
import { formatDateTimeIST } from '../utils/dateFormatter';

// Currency formatter with truncation to 2 decimals (no rounding)
const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') {
    return `₹${formattedWhole}`;
  }
  return `₹${formattedWhole}.${decimalPart}`;
};


const RedemptionRequestsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // State for API data
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search state - initialize from URL or empty
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);
  
  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance', 
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    return roleMapping[authRole] || null;
  })();
  
  // Check if user has access (Finance and SuperAdmin only)
  const canAccess = currentRole === 'Finance' || currentRole === 'SuperAdmin';
  
  const PAGE_SIZE = 25;
  // Initialize page from URL or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0
  });
  
  // Track last URL update to prevent unnecessary updates
  const lastUrlUpdateRef = useRef((() => {
    const initialPage = searchParams.get('page');
    const initialSearch = searchParams.get('search');
    return {
      page: initialPage || null,
      search: initialSearch || null
    };
  })());
  
  // Track if initial mount is complete to prevent duplicate calls
  const isInitialMountRef = useRef(true);
  // Track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef({ page: null, search: null, role: null });
  // Track if API call is in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);
  
  // Update URL when page or search changes
  useEffect(() => {
    const newPageStr = page > 1 ? page.toString() : null;
    const newSearchStr = search.trim() || null;
    
    if (lastUrlUpdateRef.current.page !== newPageStr || lastUrlUpdateRef.current.search !== newSearchStr) {
      const params = new URLSearchParams();
      if (newPageStr) {
        params.set('page', newPageStr);
      }
      if (newSearchStr) {
        params.set('search', newSearchStr);
      }
      setSearchParams(params, { replace: true });
      lastUrlUpdateRef.current = { page: newPageStr, search: newSearchStr };
    }
  }, [page, search, setSearchParams]);

  // Handle search submission (Enter key or debounce)
  const handleSearchSubmit = useCallback(() => {
    const newSearch = searchInput.trim();
    if (newSearch !== search) {
      setSearch(newSearch);
      setPage(1); // Reset to page 1 when search changes
    }
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [searchInput, search]);

  // Debounce search input (2 seconds)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearchSubmit();
    }, 2000);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, handleSearchSubmit]);

  // Load redemption requests from API
  useEffect(() => {
    // Skip on initial mount if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Skip if not authenticated or no role
    if (!isAuthenticated || !currentRole) {
      return;
    }
    
    // Use page from state (which is initialized from URL on mount)
    const currentPage = page || 1;
    const currentSearch = search.trim() || '';
    
    // Prevent duplicate API calls with same parameters
    if (lastApiCallRef.current.page === currentPage && 
        lastApiCallRef.current.search === currentSearch &&
        lastApiCallRef.current.role === currentRole) {
      return;
    }
    
    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }
    
    // Update last API call reference
    lastApiCallRef.current = { page: currentPage, search: currentSearch, role: currentRole };
    
    const loadRedemptionRequests = async () => {
      // Mark as loading
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Build query parameters
        const params = {
          page: currentPage,
          items: PAGE_SIZE
        };
        
        // Add search parameter if search query exists
        if (currentSearch) {
          params.search = currentSearch;
        }
        
        const response = await getRedemptionRequestsList(currentRole, params);
        
        let redemptionData = null;
        let paginationData = null;
        
        // Handle response structure: { items: [...], pagination: {...} } (root level)
        if (response && response.items && Array.isArray(response.items)) {
          redemptionData = response.items;
          if (response.pagination) {
            paginationData = response.pagination;
          }
        } else if (response && response.msg) {
          // Alternative response structure: { msg: { items: [...], pagination: {...} } }
          if (response.msg.items && Array.isArray(response.msg.items)) {
            redemptionData = response.msg.items;
          }
          if (response.msg.pagination) {
            paginationData = response.msg.pagination;
          }
        } else if (response && response.data) {
          // Another alternative: { data: { items: [...], pagination: {...} } }
          if (response.data.items && Array.isArray(response.data.items)) {
            redemptionData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        } else if (response && response.success && response.data) {
          // Another alternative: { success: true, data: { items: [...], pagination: {...} } }
          if (response.data.items && Array.isArray(response.data.items)) {
            redemptionData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        } else if (Array.isArray(response)) {
          // Fallback: if response is directly an array
          redemptionData = response;
          paginationData = {
            page: 1,
            items: PAGE_SIZE,
            total: response.length,
            total_pages: Math.ceil(response.length / PAGE_SIZE)
          };
        }
        
        if (redemptionData && Array.isArray(redemptionData)) {
          setRedemptions(redemptionData);
          if (paginationData) {
            // Calculate total_pages from total and items (backend doesn't provide total_pages)
            const calculatedTotalPages = Math.ceil(paginationData.total / paginationData.items);
            setPagination({
              ...paginationData,
              total_pages: calculatedTotalPages
            });
          } else {
            // Default pagination if not provided
            setPagination({
              page: currentPage,
              items: PAGE_SIZE,
              total: redemptionData.length,
              total_pages: Math.ceil(redemptionData.length / PAGE_SIZE)
            });
          }
        } else {
          // Empty response - no redemption requests
          setRedemptions([]);
          setPagination({
            page: 1,
            items: PAGE_SIZE,
            total: 0,
            total_pages: 0
          });
        }
        
      } catch (error) {
        console.error('Error loading redemption requests:', error);
        setError('Failed to load redemption requests. Please try again.');
        setRedemptions([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      }
    };

    loadRedemptionRequests();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search]);
  
  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Check access for redemption requests (Finance and SuperAdmin only)
  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Redemption Requests. This feature is only available for Finance and SuperAdmin roles.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/subscriber-wallet')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

      <div className="bg-white rounded-md shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">Redemption Requests</span>
            </h2>
          </div>
          <div className="mt-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Name, Mobile, or REF CODE..."
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit();
                  }
                }}
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="px-6 py-4 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {redemptions.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                <p className="mb-2">No redemption requests found.</p>
                <p className="text-sm text-gray-400">
                  {search.trim() 
                    ? 'Try adjusting your search criteria.' 
                    : 'No subscribers with pending redemption requests.'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-md min-w-max">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Subscriber</th>
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Mobile</th>
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">REF CODE</th>
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Pending Requests</th>
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Total Pending Amount</th>
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Latest Request Date</th>
                        <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Wallet Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redemptions.map((r) => (
                        <tr 
                          key={r.id || r.ref_code} 
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            // Navigate to redemption subscriber detail page
                            if (r.id) {
                              navigate(`/subscriber-wallet/redemption-requests/subscriber/${r.id}`);
                            }
                          }}
                        >
                          <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                            {r.personal?.full_name || r.full_name || 'N/A'}
                          </td>
                          <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                            {r.personal?.mobile || r.mobile || 'N/A'}
                          </td>
                          <td className="px-3 sm:px-6 py-2 font-mono text-xs text-gray-900 leading-tight whitespace-nowrap">
                            {r.ref_code || 'N/A'}
                          </td>
                          <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              {r.pending_requests_count || 0}
                            </span>
                          </td>
                          <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 leading-tight whitespace-nowrap">
                            {currencyRounded(r.total_pending_amount || 0)}
                          </td>
                          <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                            {r.latest_request_date ? formatDateTimeIST(r.latest_request_date) : 'N/A'}
                          </td>
                          <td className="px-3 sm:px-6 py-2 font-semibold text-xs text-gray-900 leading-tight whitespace-nowrap">
                            {currencyRounded(r.wallet_balance || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {pagination.total > 0 && (
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Showing {((pagination.page - 1) * pagination.items) + 1}-{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        disabled={pagination.page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                      >
                        Prev
                      </button>
                      {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                        const pageNum = i + 1;
                        // Show first page, last page, current page, and pages around current
                        const totalPages = pagination.total_pages || 1;
                        const shouldShow = 
                          pageNum === 1 || 
                          pageNum === totalPages || 
                          (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                        
                        if (!shouldShow) {
                          // Show ellipsis
                          if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                            return (
                              <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                                ...
                              </span>
                            );
                          }
                          return null;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        disabled={pagination.page >= (pagination.total_pages || 1)}
                        onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                        className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default RedemptionRequestsList;

